// ==================== FILE: src/components/FlowBuilder/TemplatesDialog.jsx ====================
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Box,
  Text,
  Button,
  Flex,
} from '@chakra-ui/react';
import {
  FiFolder,
  FiSearch,
  FiShoppingCart,
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiWifi,
  FiCpu,
  FiPlay,
  FiChevronRight,
} from 'react-icons/fi';
import { templateExamples } from './utils/templateExamples';

export default function TemplatesDialog({ isOpen, onClose, onLoadTemplate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ecommerce');

  const categoryIcons = {
    ecommerce: FiShoppingCart,
    marketing: FiTrendingUp,
    banking: FiDollarSign,
    saas: FiPackage,
    telecom: FiWifi,
    ai: FiCpu,
  };

  const categoryColors = {
    ecommerce: 'blue',
    marketing: 'purple',
    banking: 'green',
    saas: 'orange',
    telecom: 'cyan',
    ai: 'red',
  };

  const filteredTemplates = templateExamples[selectedCategory]?.filter(
    (template) =>
      searchQuery === '' ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <FiFolder />
            <Text>Choose a Template Flow</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Search Bar */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            {/* Category Tabs */}
            <Tabs
              colorScheme="blue"
              index={Object.keys(templateExamples).indexOf(selectedCategory)}
              onChange={(index) => setSelectedCategory(Object.keys(templateExamples)[index])}
            >
              <TabList overflowX="auto" flexWrap="nowrap">
                {Object.keys(templateExamples).map((category) => {
                  const IconComponent = categoryIcons[category];
                  return (
                    <Tab key={category} minW="fit-content">
                      <HStack spacing={1}>
                        <IconComponent size={14} />
                        <Text fontSize="sm" textTransform="capitalize">
                          {category}
                        </Text>
                      </HStack>
                    </Tab>
                  );
                })}
              </TabList>

              <TabPanels>
                {Object.entries(templateExamples).map(([category, templates]) => (
                  <TabPanel key={category}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} maxH="50vh" overflowY="auto">
                      {filteredTemplates?.map((template) => {
                        const color = categoryColors[category];
                        return (
                          <Box
                            key={template.id}
                            p={5}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            _hover={{
                              shadow: 'lg',
                              borderColor: `${color}.400`,
                              transform: 'translateY(-2px)',
                            }}
                            transition="all 0.2s"
                          >
                            <VStack align="start" spacing={4}>
                              {/* Template Header */}
                              <Flex justify="space-between" w="full">
                                <Box flex={1}>
                                  <Text fontWeight="medium" fontSize="md" noOfLines={1}>
                                    {template.name}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600" noOfLines={2} mt={1}>
                                    {template.description}
                                  </Text>
                                </Box>
                                <Box ml={2}>
                                  {React.createElement(categoryIcons[category], {
                                    size: 20,
                                    color: `var(--chakra-colors-${color}-500)`,
                                  })}
                                </Box>
                              </Flex>

                              {/* Mini Flow Preview */}
                              <Box bg="gray.50" rounded="md" p={3} border="1px" borderColor="gray.200" w="full">
                                <Flex align="center" justify="center" gap={1.5} fontSize="xs" flexWrap="wrap">
                                  <Flex align="center" gap={1}>
                                    <Box w={2} h={2} rounded="full" bg="green.500" />
                                    <Text color="gray.600">Start</Text>
                                  </Flex>
                                  <FiChevronRight size={12} color="gray" />
                                  <Flex align="center" gap={1}>
                                    <Box w={2} h={2} rounded="full" bg="blue.500" />
                                    <Text color="gray.600">Send</Text>
                                  </Flex>
                                  <FiChevronRight size={12} color="gray" />
                                  <Flex align="center" gap={1}>
                                    <Box w={2} h={2} rounded="full" bg="purple.500" />
                                    <Text color="gray.600">Wait</Text>
                                  </Flex>
                                  <FiChevronRight size={12} color="gray" />
                                  <Flex align="center" gap={1}>
                                    <Box w={2} h={2} rounded="full" bg="orange.500" />
                                    <Text color="gray.600">Check</Text>
                                  </Flex>
                                  <FiChevronRight size={12} color="gray" />
                                  <Flex align="center" gap={1}>
                                    <Box w={2} h={2} rounded="full" bg="gray.400" />
                                    <Text color="gray.600">End</Text>
                                  </Flex>
                                </Flex>
                              </Box>

                              {/* Load Button */}
                              <Button
                                size="sm"
                                colorScheme={color}
                                leftIcon={<FiPlay />}
                                w="full"
                                onClick={() => onLoadTemplate(template.id)}
                              >
                                Load on Canvas
                              </Button>
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>

                    {/* No Results Message */}
                    {filteredTemplates?.length === 0 && (
                      <VStack spacing={3} py={12} color="gray.500">
                        <FiSearch size={48} opacity={0.3} />
                        <Text>No templates found matching "{searchQuery}"</Text>
                      </VStack>
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}